import type { ComponentType } from "react";
import { TableHeader } from "../reports/Table";
import { classNames } from "../utils";
import LoadingSpinner from "./LoadingSpinner";

export interface LazyLoadRowProps<T> {
  parentRow: T;
  index: number;
}

export interface LazyLoadTableProps<T> {
  parentRows: T[] | undefined;
  parentLoading: boolean;
  headers: TableHeader[];
  emptyMessage: string;
  keyField: string;
  rowFunction: ComponentType<LazyLoadRowProps<T>>;
}

export const LazyLoadTable = <T,>({
  parentRows,
  parentLoading,
  headers,
  emptyMessage,
  keyField,
  rowFunction,
}: LazyLoadTableProps<T>) => {
  const RowComponent = rowFunction;

  return (
    <div>
      <div className="flex text-gray-500 border rounded-t-xl shadow shadow-gray-300">
        {headers.map((header, i) => (
          <div
            className={classNames(
              "flex p-3 text-xs leading-4 font-medium bg-gray-50 uppercase",
              typeof header === "string" ? "flex-1" : header.customClass
            )}
            key={i}
          >
            {typeof header === "string" ? header : header.text}
          </div>
        ))}
      </div>
      <div className="bg-white border-l border-r rounded-b-xl shadow shadow-gray-300">
        {parentLoading && <LoadingSpinner />}
        {!parentLoading && (!parentRows || parentRows.length === 0) ? (
          <div className="p-6 text-center">{emptyMessage}</div>
        ) : (
          parentRows?.map((row, i) => (
            <div key={String(row[keyField as keyof T])}>
              <RowComponent parentRow={row} index={i} />
            </div>
          ))
        )}
      </div>
    </div>
  );
};
